import React from 'react';
import { LegalPageLayout, LegalSection, LegalCallout } from '../components/LegalPageLayout';

const TOC = [
  { id: 'parties', label: 'Parties & Roles' },
  { id: 'subject', label: 'Subject Matter & Duration' },
  { id: 'nature', label: 'Nature & Purpose of Processing' },
  { id: 'categories', label: 'Data Subjects & Data Categories' },
  { id: 'obligations', label: 'Processor Obligations' },
  { id: 'subprocessors', label: 'Subprocessors' },
  { id: 'assistance', label: 'Assistance & Breach Notification' },
  { id: 'transfers', label: 'International Transfers' },
  { id: 'deletion', label: 'Deletion & Return of Data' },
  { id: 'audit', label: 'Audit Rights' },
  { id: 'execution', label: 'Executing this DPA' },
];

const DataProcessingAgreement = () => (
  <LegalPageLayout title="Data Processing Agreement (DPA)" lastUpdated="August 5, 2026" toc={TOC}>
    <LegalSection id="parties" number={1} title="Parties & Roles">
      <p>
        This Data Processing Agreement (&ldquo;DPA&rdquo;) forms part of the agreement between BookingFlows
        (&ldquo;Processor&rdquo;) and the business using the platform (the &ldquo;Operator&rdquo; or
        &ldquo;Controller&rdquo;) under our <a href="/terms">Terms of Service</a>. It applies wherever
        BookingFlows processes personal data of the Operator&apos;s customers on the Operator&apos;s behalf —
        including under the GDPR (EU/UK) and the UAE Personal Data Protection Law (PDPL).
      </p>
      <p>
        As described in our <a href="/privacy">Privacy Policy</a>, the Operator is the data controller
        for customer booking data; BookingFlows processes that data solely on the Operator&apos;s
        documented instructions.
      </p>
    </LegalSection>

    <LegalSection id="subject" number={2} title="Subject Matter & Duration">
      <p>
        The subject matter of processing is the operation of the BookingFlows platform: bookings,
        ticketing, waivers, payments facilitation, scheduling, and customer notifications. Processing
        continues for the duration of the Operator&apos;s use of the platform and thereafter only as needed
        for deletion/return obligations (Section 9) and legal retention requirements.
      </p>
    </LegalSection>

    <LegalSection id="nature" number={3} title="Nature & Purpose of Processing">
      <ul>
        <li>Collection and storage of booking and customer records entered by the Operator or its customers;</li>
        <li>Transmission of transactional notifications (email/SMS) at the Operator&apos;s direction;</li>
        <li>Facilitation of payments through the Operator&apos;s connected payment account;</li>
        <li>Generation of manifests, reports, and exports for the Operator;</li>
        <li>Hosting, backup, and security of the foregoing.</li>
      </ul>
    </LegalSection>

    <LegalSection id="categories" number={4} title="Data Subjects & Data Categories">
      <ul>
        <li><strong>Data subjects:</strong> the Operator&apos;s customers and end users who book, sign waivers, or receive communications; the Operator&apos;s team members.</li>
        <li><strong>Data categories:</strong> name, email address, phone number, booking details (dates, party size, service), waiver signatures and related verification data, payment references (never full card numbers), and communication history.</li>
        <li><strong>Special categories:</strong> not intentionally collected; Operators must not use free-text fields to collect sensitive data.</li>
      </ul>
    </LegalSection>

    <LegalSection id="obligations" number={5} title="Processor Obligations">
      <p>BookingFlows will:</p>
      <ul>
        <li>Process personal data only on the Operator&apos;s documented instructions, including with regard to international transfers;</li>
        <li>Ensure persons authorized to process the data are bound by confidentiality;</li>
        <li>Implement appropriate technical and organizational measures as described in our <a href="/security-policy">Security Policy</a> (encryption in transit and at rest, tenant isolation, access controls, monitoring, daily encrypted backups);</li>
        <li>Respect the conditions in Sections 6 (subprocessors) and 8 (transfers);</li>
        <li>Assist the Operator as described in Section 7;</li>
        <li>Delete or return personal data as described in Section 9;</li>
        <li>Make available information necessary to demonstrate compliance as described in Section 10.</li>
      </ul>
    </LegalSection>

    <LegalSection id="subprocessors" number={6} title="Subprocessors">
      <p>
        The Operator provides general authorization for BookingFlows to engage subprocessors (e.g.
        hosting, payments, email/SMS delivery) as listed in the subprocessors section of our{' '}
        <a href="/privacy">Privacy Policy</a>. BookingFlows will impose data protection obligations on
        subprocessors no less protective than this DPA and remains responsible for their performance.
        We will update the published list when subprocessors change; Operators with executed enterprise
        DPAs may request advance notice of material changes.
      </p>
    </LegalSection>

    <LegalSection id="assistance" number={7} title="Assistance & Breach Notification">
      <ul>
        <li>Taking into account the nature of processing, BookingFlows will assist the Operator with data subject requests (access, correction, deletion, portability) concerning data held on the platform;</li>
        <li>BookingFlows will assist with the Operator&apos;s obligations regarding security, breach notification, and data protection impact assessments, to the extent the relevant information is available to us;</li>
        <li>BookingFlows will notify the Operator <strong>without undue delay</strong> after becoming aware of a personal data breach affecting the Operator&apos;s data, and will provide information reasonably required for the Operator&apos;s own notification obligations.</li>
      </ul>
    </LegalSection>

    <LegalSection id="transfers" number={8} title="International Transfers">
      <p>
        Where personal data originating from the EU/UK is transferred to countries without an adequacy
        decision, the parties rely on appropriate safeguards — including the European Commission&apos;s
        Standard Contractual Clauses (and the UK Addendum where applicable), which are incorporated by
        reference into executed enterprise DPAs. For UAE Operators, transfers are handled consistent
        with the PDPL&apos;s cross-border transfer requirements.
      </p>
    </LegalSection>

    <LegalSection id="deletion" number={9} title="Deletion & Return of Data">
      <p>
        Upon termination of the Operator&apos;s account, BookingFlows will, at the Operator&apos;s choice, delete
        or return the Operator&apos;s customer personal data, and delete remaining copies within a reasonable
        period — except where retention is required by law (e.g. financial transaction records) or held
        in encrypted backups pending scheduled rotation.
      </p>
    </LegalSection>

    <LegalSection id="audit" number={10} title="Audit Rights">
      <p>
        BookingFlows will make available information reasonably necessary to demonstrate compliance with
        this DPA — including summaries of our security practices — and will allow for and contribute to
        audits conducted by the Operator or its mandated auditor, subject to reasonable notice,
        confidentiality, and frequency limits agreed with enterprise customers.
      </p>
    </LegalSection>

    <LegalSection id="execution" number={11} title="Executing this DPA">
      <LegalCallout>
        <p>
          This page is our standard DPA and applies automatically to all Operators as part of the Terms
          of Service. Enterprise customers who require a countersigned copy (including executed Standard
          Contractual Clauses) can request one through our <a href="/support#tickets">Support Center</a>.
        </p>
      </LegalCallout>
    </LegalSection>
  </LegalPageLayout>
);

export default DataProcessingAgreement;
